// The monitoring policy's words, read into a structure a page can draw (module 10 U129).
//
// The policy is stored as one text (rails.monitoring_policy.body) and a published version is
// frozen, so whatever structure it has must live IN the words. This reads a small, fixed set
// of marks and nothing else:
//
//   # Title              the policy's name, once, at the top
//   ## Section           a section heading
//   ### Part             a heading inside a section
//   - item               a bulleted list (also "* " and "• ")
//   1. item              a numbered list, numbered from the author's first number
//   **words**            bold, inside any of the above
//   (a blank line)       ends a paragraph or a list
//
// Three rules the tests hold it to:
//   - nothing is lost: every character of the words that is not one of the marks above comes
//     out in some block, in order. The words are the company's and are shown whole (R22);
//   - nothing is interpreted that is not on the list: no links, no HTML, no images. What it
//     does not know stays as the author typed it. The page draws React text nodes, never HTML;
//   - words with no marks at all read exactly as they did before: paragraphs on blank lines,
//     with an author's own line breaks kept.

/** A run of words, bold or not. */
export interface Run { text: string; bold: boolean }

export type Block =
  | { kind: 'title' | 'section' | 'part' | 'para'; runs: Run[] }
  | { kind: 'list'; ordered: boolean; start: number; items: Run[][] };

const HEADING = /^(#{1,3})[ \t]+(.+?)[ \t]*#*[ \t]*$/u;
const BULLET = /^[ \t]{0,3}[-*•][ \t]+(.*)$/u;
const NUMBERED = /^[ \t]{0,3}(\d{1,3})[.)][ \t]+(.*)$/u;
const CONTINUES = /^[ \t]{2,}\S/u;

/** `**bold**` and nothing else. A pair that never closes stays as it was typed. */
export function runsOf(text: string): Run[] {
  const runs: Run[] = [];
  let rest = text;
  for (;;) {
    const open = rest.indexOf('**');
    const close = open < 0 ? -1 : rest.indexOf('**', open + 2);
    if (open < 0 || close < 0 || close === open + 2) break;
    if (open > 0) runs.push({ text: rest.slice(0, open), bold: false });
    runs.push({ text: rest.slice(open + 2, close), bold: true });
    rest = rest.slice(close + 2);
  }
  if (rest !== '' || runs.length === 0) runs.push({ text: rest, bold: false });
  return runs;
}

/** The policy's words as blocks, in order. */
export function policyBlocks(body: string): Block[] {
  const blocks: Block[] = [];
  let para: string[] = [];
  let list: { ordered: boolean; start: number; items: string[] } | null = null;

  const endPara = () => {
    if (para.length > 0) blocks.push({ kind: 'para', runs: runsOf(para.join('\n')) });
    para = [];
  };
  const endList = () => {
    if (list) blocks.push({ kind: 'list', ordered: list.ordered, start: list.start, items: list.items.map(runsOf) });
    list = null;
  };

  for (const raw of body.replace(/\r\n?/gu, '\n').split('\n')) {
    const line = raw.replace(/[ \t]+$/u, '');
    if (line.trim() === '') { endPara(); endList(); continue; }

    const h = HEADING.exec(line);
    if (h) {
      endPara(); endList();
      blocks.push({ kind: h[1]!.length === 1 ? 'title' : h[1]!.length === 2 ? 'section' : 'part', runs: runsOf(h[2]!) });
      continue;
    }

    const b = BULLET.exec(line);
    const n = b ? null : NUMBERED.exec(line);
    if (b || n) {
      endPara();
      const ordered = n !== null;
      if (list && list.ordered !== ordered) endList();
      if (!list) list = { ordered, start: n ? Number(n[1]) : 1, items: [] };
      list.items.push(b ? b[1]! : n![2]!);
      continue;
    }

    // An indented line under a list item carries that item on; anything else ends the list.
    if (list && CONTINUES.test(line)) {
      list.items[list.items.length - 1] += ` ${line.trim()}`;
      continue;
    }
    endList();
    para.push(line);
  }
  endPara(); endList();
  return blocks;
}

/** The words with the marks taken off, on one line: for an excerpt in a table. */
export function policyPlain(body: string): string {
  const say = (runs: Run[]) => runs.map((r) => r.text).join('');
  return policyBlocks(body)
    .map((b) => (b.kind === 'list' ? b.items.map(say).join(' · ') : say(b.runs)))
    .join(' ')
    .replace(/\s+/gu, ' ')
    .trim();
}

/** The section headings, in order: what a reader can jump to. */
export function policySections(blocks: readonly Block[]): string[] {
  return blocks.flatMap((b) => (b.kind === 'section' ? [b.runs.map((r) => r.text).join('')] : []));
}
